import { Box, Link } from "@mui/material";

/**
 * Shown on Prepare Content when the quote has no tab rows. Resolves earlier versions
 * (same quote number) that still have tab data and lists zero-tab versions as broken.
 */
export function QuotePrepareContentRecoverPanel() {
    var quote = app.currentQuote;
    var currentVersion = parseInt(quote.Version, 10);
    if (isNaN(currentVersion)) {
        currentVersion = 1;
    }

    var previousIdState = React.useState(null);
    var previousIdQuoteMain = previousIdState[0];
    var setPreviousIdQuoteMain = previousIdState[1];
    var previousVersionState = React.useState(null);
    var previousResolvedVersion = previousVersionState[0];
    var setPreviousResolvedVersion = previousVersionState[1];
    var brokenState = React.useState([]);
    var brokenPreviousVersions = brokenState[0];
    var setBrokenPreviousVersions = brokenState[1];
    var lookupState = React.useState(currentVersion > 1 ? "loading" : "none");
    var prevLookupStatus = lookupState[0];
    var setPrevLookupStatus = lookupState[1];

    React.useEffect(
        function () {
            if (currentVersion <= 1) {
                setPreviousIdQuoteMain(null);
                setPreviousResolvedVersion(null);
                setBrokenPreviousVersions([]);
                setPrevLookupStatus("none");
                return undefined;
            }
            setPreviousIdQuoteMain(null);
            setPreviousResolvedVersion(null);
            setBrokenPreviousVersions([]);
            setPrevLookupStatus("loading");
            var cancelled = false;
            var brokenAccum = [];

            function queryQuoteMainForVersion(ver, onDone) {
                var getMain = quosal.api.data.query({
                    table: "QuoteMain",
                    where: [
                        { field: "QuoteReadableId", operator: "Equals", value: quote.QuoteReadableId },
                        { field: "Version", operator: "Equals", value: ver }
                    ]
                });
                getMain.finished = function (msg) {
                    if (cancelled) {
                        return;
                    }
                    if (msg.error) {
                        onDone({ error: true });
                        return;
                    }
                    var row = msg.resultSets && msg.resultSets[0] && msg.resultSets[0][0];
                    onDone({ row: row && row.IdQuoteMain ? row : null });
                };
                getMain.call();
            }

            function queryHasAnyTabs(idQuoteMain, onDone) {
                var getTabs = quosal.api.data.query({
                    table: "QuoteTabs",
                    where: [{ field: "IdQuoteMain", operator: "Equals", value: idQuoteMain }]
                });
                getTabs.finished = function (msg) {
                    if (cancelled) {
                        return;
                    }
                    if (msg.error) {
                        onDone({ error: true });
                        return;
                    }
                    var rows = (msg.resultSets && msg.resultSets[0]) || [];
                    onDone({ hasTabs: rows.length > 0 });
                };
                getTabs.call();
            }

            function tryVersion(ver) {
                if (cancelled) {
                    return;
                }
                if (ver < 1) {
                    setBrokenPreviousVersions(brokenAccum.slice());
                    setPreviousIdQuoteMain(null);
                    setPreviousResolvedVersion(null);
                    setPrevLookupStatus("none");
                    return;
                }
                queryQuoteMainForVersion(ver, function (mainResult) {
                    if (cancelled) {
                        return;
                    }
                    if (mainResult.error) {
                        setBrokenPreviousVersions([]);
                        setPrevLookupStatus("error");
                        return;
                    }
                    if (!mainResult.row) {
                        tryVersion(ver - 1);
                        return;
                    }
                    var mainRow = mainResult.row;
                    queryHasAnyTabs(mainRow.IdQuoteMain, function (tabResult) {
                        if (cancelled) {
                            return;
                        }
                        if (tabResult.error) {
                            setBrokenPreviousVersions([]);
                            setPrevLookupStatus("error");
                            return;
                        }
                        if (tabResult.hasTabs) {
                            setBrokenPreviousVersions(brokenAccum.slice());
                            setPreviousIdQuoteMain(mainRow.IdQuoteMain);
                            setPreviousResolvedVersion(ver);
                            setPrevLookupStatus("found");
                        } else {
                            brokenAccum.push({
                                version: ver,
                                idQuoteMain: mainRow.IdQuoteMain,
                                quoteReadableId:
                                    mainRow.QuoteReadableId != null ? mainRow.QuoteReadableId : quote.QuoteReadableId,
                                quoteName: mainRow.QuoteName != null ? mainRow.QuoteName : quote.QuoteName
                            });
                            setBrokenPreviousVersions(brokenAccum.slice());
                            tryVersion(ver - 1);
                        }
                    });
                });
            }

            tryVersion(currentVersion - 1);

            return function () {
                cancelled = true;
            };
        },
        [quote.IdQuoteMain, quote.QuoteReadableId, quote.QuoteName, currentVersion]
    );

    function quoteRecoverNavState() {
        return { rand: quosal.util.generateGuid() };
    }

    function navigateVersionList() {
        quosal.navigation.navigate(quosal.util.url("versionlist.quosalweb"), quoteRecoverNavState());
    }

    function navigateQuoteHome(idQuoteMain) {
        var dashboardModule = quosal.sell.modules.find("quote.dashboard");
        if (dashboardModule) {
            dashboardModule.load({
                query: "idquotemain=" + idQuoteMain + "&submodules=quote.home",
                forceNavigation: true
            });
            return;
        }
        quosal.navigation.navigate(
            quosal.util.url("quote.dashboard", "submodules=quote.home", "idquotemain=" + idQuoteMain),
            quoteRecoverNavState()
        );
    }

    var noPreviousVersionsMessage =
        "There are no previous valid versions of this quote. Please delete this quote and create a new one.";

    var brokenDisplayList = [];
    if ((prevLookupStatus === "found" || prevLookupStatus === "none") && currentVersion > 1) {
        brokenDisplayList = [
            {
                isCurrent: true,
                version: currentVersion,
                idQuoteMain: quote.IdQuoteMain,
                quoteReadableId: quote.QuoteReadableId
            }
        ].concat(brokenPreviousVersions);
    }

    return (
        <Box
            sx={{
                maxWidth: 660,
                mx: "auto",
                my: 0,
                px: { xs: 2, sm: 3 },
                py: 3,
                borderRadius: 1,
                border: 1,
                borderColor: "divider",
                bgcolor: "background.paper",
                boxShadow: 1
            }}
        >
                    <Box
                        component="p"
                        sx={{
                            mt: 0,
                            mb: 2,
                            typography: "body1",
                            color: "text.primary",
                            lineHeight: 1.6
                        }}
                    >
                        Prepare Content cannot load because of an error during the versioning process.
                    </Box>
                    {currentVersion > 1 && prevLookupStatus === "loading" ? (
                        <Box
                            sx={{
                                typography: "body2",
                                color: "text.secondary",
                                fontStyle: "italic",
                                mb: 2
                            }}
                        >
                            Looking up earlier versions with valid data…
                        </Box>
                    ) : null}
                    {brokenDisplayList.length > 0 ? (
                        <Box sx={{ mb: 2 }}>
                            <Box
                                component="p"
                                sx={{
                                    mt: 0,
                                    mb: 1,
                                    typography: "body2",
                                    color: "text.secondary",
                                    lineHeight: 1.6
                                }}
                            >
                                These versions have errors.
                            </Box>
                            <Box
                                component="ul"
                                sx={{
                                    m: 0,
                                    mb: 1.5,
                                    pl: 2.5,
                                    typography: "body2",
                                    color: "text.primary",
                                    lineHeight: 1.8
                                }}
                            >
                                {brokenDisplayList.map(function (b) {
                                    var readable =
                                        b.quoteReadableId != null ? String(b.quoteReadableId) : String(quote.QuoteReadableId);
                                    var label = "#" + readable + " v" + b.version;
                                    return (
                                        <Box component="li" key={(b.isCurrent ? "current-" : "") + b.version + "-" + b.idQuoteMain}>
                                            {label}{" "}
                                            {b.isCurrent ? "(current, broken)" : "(broken)"}
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                    ) : null}
                    {currentVersion > 1 &&
                    prevLookupStatus === "found" &&
                    previousIdQuoteMain &&
                    previousResolvedVersion != null ? (
                        <Box sx={{ mb: 2 }}>
                            <Box
                                component="p"
                                sx={{
                                    mt: 0,
                                    mb: 1.5,
                                    typography: "body2",
                                    color: "text.secondary",
                                    lineHeight: 1.6
                                }}
                            >
                                Most recent working version found, open and re-version the quote to restore a working copy.
                            </Box>
                            <Link
                                component="button"
                                type="button"
                                underline="always"
                                onClick={function () {
                                    navigateQuoteHome(previousIdQuoteMain);
                                }}
                                sx={{
                                    typography: "body1",
                                    fontWeight: 600,
                                    display: "inline-block",
                                    verticalAlign: "baseline",
                                    cursor: "pointer",
                                    p: 0,
                                    border: "none",
                                    background: "none",
                                    fontFamily: "inherit"
                                }}
                            >
                                Open previous version (v{previousResolvedVersion})
                            </Link>
                        </Box>
                    ) : null}
                    {currentVersion <= 1 ||
                    (currentVersion > 1 && (prevLookupStatus === "none" || prevLookupStatus === "error")) ? (
                        <Box
                            component="p"
                            sx={{
                                mt: 0,
                                mb: 2,
                                p: 2,
                                borderRadius: 1,
                                bgcolor: "grey.100",
                                border: 1,
                                borderColor: "grey.300",
                                typography: "body2",
                                color: "text.primary",
                                lineHeight: 1.6
                            }}
                        >
                            {noPreviousVersionsMessage}
                        </Box>
                    ) : null}
                    {currentVersion > 1 &&
                    prevLookupStatus === "found" &&
                    previousIdQuoteMain &&
                    previousResolvedVersion != null ? (
                        <Box
                            sx={{
                                mt: 1,
                                pt: 2,
                                borderTop: 1,
                                borderColor: "divider"
                            }}
                        >
                            <Link
                                component="button"
                                type="button"
                                underline="always"
                                onClick={navigateVersionList}
                                sx={{
                                    typography: "body1",
                                    fontWeight: 600,
                                    verticalAlign: "baseline",
                                    cursor: "pointer",
                                    p: 0,
                                    border: "none",
                                    background: "none",
                                    fontFamily: "inherit"
                                }}
                            >
                                View all versions
                            </Link>
                        </Box>
                    ) : null}
        </Box>
    );
}
